import _get from 'lodash/get';
import { Cookies } from 'react-cookie';
import { extractErrorMessage, genID } from 'ravenjs/utils';
import { toast } from 'ravenjs/lib';
import { REDIS_VALUE, FEIN_MODAL } from 'constants/acaReporting';
import { MODALS } from 'constants/modals';
import { EVENTS } from 'constants/events';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';

const cookies = new Cookies();
const year = new Date().getFullYear() - 1;

/**
 * Opens the FEIN modal for PCORI reporting.
 * Shows a prospect/disabled message or an "Update FEIN" prompt depending on context.
 */
const openFEINModal = ({
    isProspect,
    disableCompanySettings,
    openModal,
    closeModal,
    history,
    partnerId,
    companyId,
}) => {
    const modalConfig =
        isProspect || disableCompanySettings
            ? {
                  title: FEIN_MODAL.PROSPECT_PARTNER_ADMIN_TITLE,
                  description: FEIN_MODAL.PCORI.PROSPECT_PARTNER_ADMIN_DESCRIPTION,
                  actions: [
                      {
                          text: FEIN_MODAL.BUTTONS.OK,
                          color: 'primary',
                          onClick() {
                              closeModal(MODALS.CONFIRM);
                          },
                      },
                  ],
              }
            : {
                  title: FEIN_MODAL.TITLE,
                  description: FEIN_MODAL.PCORI.DESCRIPTION,
                  actions: [
                      {
                          text: FEIN_MODAL.BUTTONS.CANCEL,
                          color: 'secondary',
                          onClick() {
                              closeModal(MODALS.CONFIRM);
                          },
                      },
                      {
                          text: FEIN_MODAL.BUTTONS.UPDATE,
                          color: 'primary',
                          onClick() {
                              history.push(
                                  `/admin/companies/${partnerId}/${companyId}/edit?isClientUser=true`
                              );
                              closeModal(MODALS.CONFIRM);
                          },
                      },
                  ],
              };

    openModal(MODALS.CONFIRM, {
        ...modalConfig,
        showTitle: true,
        newModalStyles: true,
        ModalProps: {
            ContentProps: {
                style: {
                    maxWidth: '600px',
                    padding: '24px 32px',
                    borderRadius: '0.5rem',
                    backgroundColor: MIGRATED_COLORS.white,
                },
            },
        },
    });
};

/**
 * Handles PCORI SSO redirect flow:
 * 1. Fetches company additional data
 * 2. Stores tax year in Redis with PCORI suffix
 * 3. Send Pendo event for tracking
 * 4. Opens HRLogics SSO in a new tab
 */
const performSsoRedirect = async ({
    companyId,
    fetchCompanyAdditionalData,
    setAcaTaxYearInRedis,
    sendPendoEvent,
}) => {
    const response = await fetchCompanyAdditionalData(companyId);
    const companyAdditionalData = _get(response, 'companyAdditionalData[0]', {});
    const { data: { uhcCompanyId = '', externalCompanyId = '' } = {} } = companyAdditionalData;

    const id = externalCompanyId || uhcCompanyId || companyId;
    await setAcaTaxYearInRedis({
        data: {
            value: `${year};${id};${companyId};${REDIS_VALUE.PCORI}`,
        },
    });

    const baseEvent = EVENTS.HR_LOGICS.PCORI_REPORTING_TOOL_LINK_CLICK;
    const redirect_id = genID();
    cookies.set('redirect_id', redirect_id, {
        path: '/',
        secure: true,
        sameSite: 'None',
    });
    const pendoEvent = {
        ...baseEvent,
        eventProperties: {
            ...baseEvent.eventProperties,
            companyId,
        },
        context: {
            ...baseEvent.context,
            url: `${window.location.origin}/hr-tools/aca-reporting/HRLogics?redirect_id=${redirect_id}`,
        },
    };
    if (typeof sendPendoEvent === 'function') {
        sendPendoEvent(pendoEvent);
    }
    window.open(`/hr-tools/aca-reporting/HRLogics?redirect_id=${redirect_id}`, '_blank');
};

/**
 * Handles the full PCORI SSO redirect flow with FEIN validation:
 * 1. Fetches company to check for FEIN
 * 2. If FEIN missing — shows appropriate modal
 * 3. If FEIN present — proceeds with SSO redirect
 *
 * @param {Object} params
 * @param {number} params.companyId
 * @param {number} params.partnerId
 * @param {Function} params.fetchCompany - admin fetchCompany action
 * @param {Function} params.fetchCompanyAdditionalData
 * @param {Function} params.setAcaTaxYearInRedis
 * @param {Function} params.sendPendoEvent
 * @param {Function} params.isLoading
 * @param {Function} params.openModal
 * @param {Function} params.closeModal
 * @param {Object} params.history - react-router history object
 */
export const handlePCORISsoRedirect = async ({
    companyId,
    partnerId,
    fetchCompany,
    fetchCompanyAdditionalData,
    setAcaTaxYearInRedis,
    sendPendoEvent,
    isLoading,
    openModal,
    closeModal,
    history,
}) => {
    isLoading(true);
    try {
        const companyResponse = await fetchCompany(companyId);
        const federalEIN = _get(companyResponse, 'federalEIN', null);
        const disableCompanySettings = _get(companyResponse, 'disableCompanySettings', false);
        const isProspect = _get(companyResponse, 'isProspect', false);

        if (!federalEIN) {
            isLoading(false);
            openFEINModal({
                isProspect,
                disableCompanySettings,
                openModal,
                closeModal,
                history,
                partnerId,
                companyId,
            });
            return;
        }

        await performSsoRedirect({
            companyId,
            fetchCompanyAdditionalData,
            setAcaTaxYearInRedis,
            sendPendoEvent,
        });
    } catch (error) {
        toast.error(extractErrorMessage(error));
    } finally {
        isLoading(false);
    }
};
